// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { cn } from '@/lib/utils';
import { hueFor } from '@/lib/hueFor';

export interface MonogramTileProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Drives the tile background hue deterministically via hueFor(). */
  seed: string;
  /** The monogram text to display (caller supplies initials or app abbreviation). */
  label: string;
  /** Tile width and height in px. */
  size: number;
  /**
   * Shape variant.
   * - 'circle'  → border-radius 50% (avatars)
   * - 'rounded' → proportional rounded corner (app tiles); default
   */
  shape?: 'circle' | 'rounded';
  /**
   * Desaturated/deactivated appearance for inactive or revoked states.
   */
  muted?: boolean;
}

/**
 * A generic monogram tile with a deterministic hued background.
 *
 * Background lightness is capped so white text (#fff) meets WCAG 2.1 AA (≥4.5:1)
 * across the full hue circle. At hsl(h 50% 30%) the worst-case hue is h=60 (pure
 * yellow) at ≈4.9:1; all 360 integer hues clear the 4.5:1 AA threshold for white
 * text. The muted variant hsl(h 12% 38%) clears it comfortably (≈5.3:1+) — the
 * saturation is so low that hue has negligible effect on luminance.
 *
 * Callers control aria-hidden / title / role — this component does not assume
 * the rendering context (avatar vs. decorative tile).
 */
export function MonogramTile({
  seed,
  label,
  size,
  shape = 'rounded',
  muted = false,
  className,
  style,
  ...rest
}: MonogramTileProps): React.JSX.Element {
  const hue = hueFor(seed);

  // Active: hsl(h 50% 30%) — worst-case contrast (h=60 yellow) ≈4.9:1 against #fff.
  // Muted: hsl(h 12% 38%) — desaturated; worst-case contrast ≈5.3:1+ against #fff.
  const backgroundColor = muted ? `hsl(${hue} 12% 38%)` : `hsl(${hue} 50% 30%)`;

  const borderRadius = shape === 'circle' ? '50%' : `${Math.round(size * 0.22)}px`;

  const fontSize = Math.round(size * 0.38);

  return (
    <div
      className={cn('shrink-0 font-semibold', shape === 'circle' && 'rounded-full', className)}
      style={{
        display: 'grid',
        placeItems: 'center',
        width: size,
        height: size,
        borderRadius,
        backgroundColor,
        color: '#fff',
        fontSize,
        fontWeight: 600,
        userSelect: 'none',
        ...style,
      }}
      {...rest}
    >
      {label}
    </div>
  );
}
